import React, { useEffect, useRef, useState } from 'react';
import { CircularProgress, Alert, Paper, Typography, Box, Button, Modal, Switch, FormControlLabel, Tabs, Tab } from '@mui/material';
import { formatTimestamp, formatTimeAgo } from '../util';
import {
  fetchDeviceImageAndMetadata,
  type DisplayFields
} from './viewportImageAndMetadataFetch';
import DeviceLogs from './deviceLogs';
import DeviceConfiguration from './deviceConfiguration';
import CaptureImagesButton from './captureImagesButton';
import styles from './viewportDisplay.module.css';

interface ViewportDisplayProps {
  selectedDevice: string | null;
}

type DisplayTab = 'liveView' | 'configuration';

const ViewportDisplay: React.FC<ViewportDisplayProps> = ({ selectedDevice }) => {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [displayFields, setDisplayFields] = useState<DisplayFields | null>(null);

  const [loading, setLoading] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // Latest request wins: responses from a superseded request (device or camera changed
  // mid-flight) are dropped. The ref also lets cleanup revoke the current object URL.
  const requestIdRef = useRef(0);
  const imageUrlRef = useRef<string | null>(null);

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [updating, setUpdating] = useState<boolean>(false);
  const [useSecondary, setUseSecondary] = useState<boolean>(false);

  const [activeTab, setActiveTab] = useState<DisplayTab>('liveView');


  // remove all image metadata from session storage
  const clearAllMetadataFromStorage = () => {
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith('10x_image_metadata_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  };

  const replaceImageUrl = (next: string | null) => {
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
    imageUrlRef.current = next;
    setImageUrl(next);
  };

  // fetch data and set to states for visuals; failures land in state, never thrown
  const loadImageAndMetadata = async (device: string, secondary: boolean) => {
    const requestId = ++requestIdRef.current; // take a new id; any earlier request still in flight is now stale
    try {
      const result = await fetchDeviceImageAndMetadata(device, secondary);
      if (requestId !== requestIdRef.current) {
        if (result.imageObjectUrl) URL.revokeObjectURL(result.imageObjectUrl);
        return;
      }
      replaceImageUrl(result.imageObjectUrl);
      setDisplayFields(result.displayFields);
      setImageError(result.imageError);
      setMetadataError(result.metadataError);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load device data';
      replaceImageUrl(null);
      setDisplayFields(null);
      setImageError(message);
      setMetadataError(message);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setUpdating(false);
      }
    }
  };

  // On camera toggle, fetch the images for whatever you toggle to and display them
  const handleCameraToggle = () => {
    if (!selectedDevice) return;
    const secondary = !useSecondary;
    setUseSecondary(secondary);
    setLoading(true);
    setImageError(null);
    setMetadataError(null);
    clearAllMetadataFromStorage();
    loadImageAndMetadata(selectedDevice, secondary);
  };

  // Handler for updating device image
  // Refetches the image and metadata; each endpoint does a fresh server-side lookup,
  // so there is no separate cache-update call to make.
  const handleUpdateImage = () => {
    if (!selectedDevice) return;

    setUpdating(true);
    setImageError(null);
    setMetadataError(null);
    clearAllMetadataFromStorage();

    // refetch image and metadata, preserving which ever camera is active
    loadImageAndMetadata(selectedDevice, useSecondary);
  };

  // fetch new data when device is selected from list
  useEffect(() => {
    clearAllMetadataFromStorage();

    if (!selectedDevice) {
      requestIdRef.current++; // drop any in-flight request
      replaceImageUrl(null);
      setDisplayFields(null);
      setImageError(null);
      setMetadataError(null);
      setLoading(false);
      setUpdating(false);
      return;
    }

    // clear for new device selection, always default to primary + live view
    setUseSecondary(false);
    setActiveTab('liveView');
    setUpdating(false);
    setLoading(true);
    setImageError(null);
    setMetadataError(null);
    loadImageAndMetadata(selectedDevice, /* secondary= */ false);
  }, [selectedDevice]);

  // Drop in-flight requests and free the image on unmount
  useEffect(() => () => {
    requestIdRef.current++;
    if (imageUrlRef.current) URL.revokeObjectURL(imageUrlRef.current);
  }, []);

  // Default state
  if (!selectedDevice) {
    return (
      <Box className={styles.genericState}>
        <Typography variant="h6">Select a device to view details</Typography>
      </Box>
    );
  }

  // Live View tab content: image, metadata, and logs. A missing image or metadata record
  // is shown as a warning; the camera toggle, retry, capture and logs stay usable.
  const renderLiveView = () => {
    if (loading) {
      return (
        <Box className={styles.genericState}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <>
      {/* Header */}
      <Box className={styles.cameraType}>
        <Box>
          {displayFields && (
            <Typography variant="subtitle1" color="text.secondary">
              Posted {formatTimeAgo(displayFields.lastUpdatedMinutes)}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={useSecondary}
              onChange={handleCameraToggle}
              disabled={loading || updating}
            />
          }
          label={useSecondary ? 'Secondary Camera' : 'Primary Camera'}
          labelPlacement="start"
        />
      </Box>

      {imageError && <Alert severity="warning" sx={{ mb: 1 }}>{imageError}</Alert>}
      {metadataError && <Alert severity="warning" sx={{ mb: 1 }}>{metadataError}</Alert>}

      {/* Image */}
      {imageUrl && (
        <Box className={styles.deviceImageFrame}>
          <img
            className={`${styles.deviceImage} ${useSecondary ? '' : styles.deviceImageFlipped}`}
            src={imageUrl}
            alt={`Something is wrong with ${selectedDevice}'s uploaded image...`}
          />
        </Box>
      )}

      {/* Metadata fields */}
      {displayFields && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Metadata
          </Typography>
          <Typography variant="body2">
            <strong>Timestamp:</strong> {formatTimestamp(displayFields.timestamp)}
          </Typography>
          <Typography variant="body2">
            <strong>Camera ID:</strong> {displayFields.camera_id}
          </Typography>
          <Typography variant="body2">
            <strong>Size (bytes):</strong> {displayFields.size_bytes != null ? displayFields.size_bytes.toLocaleString() : 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>Capture Duration (ms):</strong> {displayFields.capture_duration_ms ?? 'N/A'}
          </Typography>
          <Typography variant="body2">
            <strong>JPEG Quality:</strong> {displayFields.jpeg_quality ?? 'N/A'}
          </Typography>
        </Box>
      )}

      {/* Buttons only; Update Image doubles as the retry after a failed load */}
      <Box className={styles.buttonRow}>
        <Button
          variant="contained"
          onClick={handleUpdateImage}
          disabled={updating || loading}
        >
          {updating ? 'Updating...' : 'Update Image'}
        </Button>

        {displayFields && (
          <Button
            variant="outlined"
            onClick={() => setModalOpen(true)}
          >
            View Full Metadata
          </Button>
        )}

        <Button
          variant="outlined"
          onClick={() => {
            // AWS login page for further image browsing
            window.open('https://linevision.awsapps.com/start/#/?tab=accounts', '_blank');
          }}
        >
          AWS Login
        </Button>
      </Box>

      {/* Remote capture: tell the device to take a fresh pair of frames now.
          Rate-limited client-side via a post-trigger cooldown. Most useful when
          the image is missing, so it is always available. */}
      <CaptureImagesButton deviceName={selectedDevice} disabled={loading} />

      {/* Device logs panel */}
      <DeviceLogs key={selectedDevice} deviceName={selectedDevice} />

      {/* Modal to display full metadata JSON */}
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby="metadata-modal-title"
      >
        <Box className={styles.metadataModal}>
          <Typography id="metadata-modal-title" variant="h6" component="h2" gutterBottom>
            Full Metadata for {selectedDevice}
          </Typography>

          {/* Display the full JSON from sessionStorage */}
          <pre className={styles.metadataModalText}>
            {(() => {
              const key = useSecondary
                ? `10x_image_metadata_${selectedDevice}_secondary`
                : `10x_image_metadata_${selectedDevice}`;
              const raw = selectedDevice && sessionStorage.getItem(key);
              return raw ? JSON.stringify(JSON.parse(raw), null, 2) : 'No metadata available';
            })()}
          </pre>

          <Button
            onClick={() => setModalOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </Box>
      </Modal>
      </>
    );
  };

  // Main display: tabbed panel for the selected device.
  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h5" gutterBottom>
        {selectedDevice}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_event, value: DisplayTab) => setActiveTab(value)}
        sx={{ marginBottom: 2 }}
      >
        <Tab label="Live View" value="liveView" />
        <Tab label="Configuration" value="configuration" />
      </Tabs>

      {activeTab === 'liveView' && renderLiveView()}
      {activeTab === 'configuration' && (
        <DeviceConfiguration key={selectedDevice} deviceName={selectedDevice} />
      )}
    </Paper>
  );
};

export default ViewportDisplay;
